import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {DashboPromptCard} from './DashboPromptCard';
import {MOMENTOS, ROLES} from './promptTaxonomy';

const RAW = {interpolation: {escapeValue: false}};

/**
 * Los valores del filtro que tienen al menos un prompt, en el orden del producto (el mismo del
 * editor) y no en el de aparición: un chip que filtra a cero es ruido.
 */
export function presentValues(prompts, ordered, pick) {
    const present = new Set();
    (prompts || []).forEach((prompt) => pick(prompt).forEach((value) => present.add(value)));
    return ordered.filter((value) => present.has(value));
}

// El activo lleva relleno y tilde, como las pills del editor (ui-estilos: «tres señales, no una»).
function Chip({on, onClick, children}) {
    return (
        <button type="button" className={`agp-pill${on ? ' -on' : ''}`} aria-pressed={on} onClick={onClick}>
            {on && <FaIcon iconName="check"/>}
            {children}
        </button>
    );
}

function ChipGroup({label, all, options, value, onChange, labelOf}) {
    return (
        <div className="agp-filters__group" role="group" aria-label={label}>
            <span className="agp-filters__label">{label}</span>
            <div className="agp-filters__chips">
                <Chip on={value === null} onClick={() => onChange(null)}>{all}</Chip>
                {options.map((option) => (
                    <Chip key={option} on={value === option} onClick={() => onChange(option)}>
                        {labelOf(option)}
                    </Chip>
                ))}
            </div>
        </div>
    );
}

/**
 * «Sugeridos por Dashbo»: la vidriera de los genéricos. Lo propio es una herramienta y va en filas;
 * esto se explora, y por eso va en tarjetas con filtros de momento y rol.
 */
export function DashboPromptsTab({prompts, agencyName, adaptingId, onAdapt, onSeeAdapted, onView, onRate}) {
    const {t} = useTranslation();
    const [momento, setMomento] = useState(null);
    const [role, setRole] = useState(null);

    const momentoOptions = useMemo(
        () => presentValues(prompts, MOMENTOS, (prompt) => prompt.momentos || []), [prompts]);
    const roleOptions = useMemo(
        () => presentValues(prompts, ROLES, (prompt) => (prompt.role ? [prompt.role] : [])), [prompts]);

    const visible = (prompts || []).filter((prompt) => (
        (momento === null || (prompt.momentos || []).includes(momento))
        && (role === null || prompt.role === role)
    ));

    return (
        <div className="agp-dashbo">
            <p className="agp-banner">{t('agency_prompts.dashbo_banner', {agency: agencyName, ...RAW})}</p>
            {(momentoOptions.length > 0 || roleOptions.length > 0) && (
                <div className="agp-filters">
                    {momentoOptions.length > 0 && (
                        <ChipGroup label={t('agency_prompts.dashbo_filter_moment')}
                                   all={t('agency_prompts.dashbo_filter_all')}
                                   options={momentoOptions} value={momento} onChange={setMomento}
                                   labelOf={(value) => t(`prompt_library.momentos.${value}`)}/>
                    )}
                    {roleOptions.length > 0 && (
                        <ChipGroup label={t('agency_prompts.dashbo_filter_role')}
                                   all={t('agency_prompts.dashbo_filter_all')}
                                   options={roleOptions} value={role} onChange={setRole}
                                   labelOf={(value) => t(`prompt_library.roles.${value}`)}/>
                    )}
                </div>
            )}
            {/* Acá no hay buscador: lo que deja la grilla en cero son los filtros, y se sale con uno. */}
            {visible.length === 0 && (
                <div className="agp-dashbo-nothing">
                    <p className="agp-nothing">{t('agency_prompts.dashbo_no_results')}</p>
                    <button type="button" className="agp-btn -quiet"
                            onClick={() => { setMomento(null); setRole(null); }}>
                        {t('agency_prompts.dashbo_show_all')}
                    </button>
                </div>
            )}
            <div className="agp-cards">
                {visible.map((prompt) => (
                    <DashboPromptCard key={prompt.id} prompt={prompt} agencyName={agencyName}
                                      adapting={adaptingId === prompt.id} onAdapt={onAdapt}
                                      onSeeAdapted={onSeeAdapted} onView={onView} onRate={onRate}/>
                ))}
            </div>
        </div>
    );
}
